import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { entityIdFromPath, type EntityLinkVerdict, parseEntityLink } from "~/ai-chat/model/entity-link";
import { parseSaveResourceAction, SAVE_RESOURCE_LANGUAGE } from "~/ai-chat/model/save-resource-action";

export type ConversationArtifact =
  // A resource the assistant linked that lives in the library.
  | { kind: "saved"; id: string; resourceId: string; name: string }
  // A file the assistant proposed in a save-resource block that has not been saved.
  | { kind: "draft"; id: string; name: string; content: string };

// A resource saved from this chat. `fileName` and `content` are the saved proposal's and tie the resource
// back to its draft: `name` is the library display name the user may have edited, and a revision the
// assistant proposes later under the same file name differs in content. A staged document has no
// content and no draft, so its save is listed on its own.
export interface SavedArtifactRef {
  resourceId: string;
  name: string;
  fileName: string;
  content?: string;
}

export type VerifiedConversationArtifact = ConversationArtifact & { verdict: EntityLinkVerdict };

const MARKDOWN_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;
// The closing fence must start its own line (CommonMark), because the JSON body can
// legitimately contain ``` inside an escaped Markdown draft.
const SAVE_RESOURCE_BLOCK = new RegExp("^```" + SAVE_RESOURCE_LANGUAGE + "[^\\n]*\\n([\\s\\S]*?)\\n```[ \\t]*$", "gm");

// The assets an assistant surfaced in a conversation, in first-seen order. Saved ones
// come from the entity links it emits — `[name](/resources/<id>)` /
// `/resources/artifact/<id>` — since there is no per-conversation artifact endpoint, then
// from the resources saved from this chat, which the assistant doesn't necessarily link
// back; drafts come from its text save-resource blocks. A saved artifact is identified by its
// resource id, so repeats are ignored; a draft is identified by file name and a later block
// replaces its content, so it previews the newest revision. A draft saved from this chat
// becomes its saved artifact in place, so the card doesn't move; it just disappears when the
// assistant already linked that resource. A save matches its draft on file name and content,
// so a revision proposed after it stays a draft and the save is listed on its own.
export function collectConversationArtifacts(
  messages: ChatTranscriptItem[],
  savedArtifacts: SavedArtifactRef[],
): ConversationArtifact[] {
  const ordered: ConversationArtifact[] = [];
  const positions = new Map<string, number>();
  for (const item of messages) {
    for (const artifact of artifactsInMessage(item)) {
      const position = positions.get(artifact.id);
      if (position === undefined) {
        positions.set(artifact.id, ordered.length);
        ordered.push(artifact);
      } else if (artifact.kind === "draft") {
        ordered[position] = artifact;
      }
    }
  }
  const linkedIds = new Set(ordered.flatMap((artifact) => (artifact.kind === "saved" ? [artifact.resourceId] : [])));
  const placedIds = new Set(linkedIds);
  const promoted = ordered.flatMap((artifact): ConversationArtifact[] => {
    const saved = artifact.kind === "draft" ? latestSaveOf(artifact, savedArtifacts) : undefined;
    if (saved === undefined) return [artifact];
    if (linkedIds.has(saved.resourceId)) return [];
    placedIds.add(saved.resourceId);
    return [{ kind: "saved", id: artifact.id, resourceId: saved.resourceId, name: saved.name }];
  });
  for (const { resourceId, name } of savedArtifacts) {
    if (placedIds.has(resourceId)) continue;
    placedIds.add(resourceId);
    promoted.push({ kind: "saved", id: resourceId, resourceId, name });
  }
  return promoted;
}

const NO_ARTIFACTS: readonly ConversationArtifact[] = [];
// The transcript array is rebuilt on every streamed delta while each finished message keeps
// its identity, and parsing a save-resource block means a JSON parse plus a byte count of
// the draft, so results are kept per message object: only the message still streaming is
// re-parsed.
const artifactsByMessage = new WeakMap<ChatTranscriptItem, readonly ConversationArtifact[]>();

function artifactsInMessage(item: ChatTranscriptItem): readonly ConversationArtifact[] {
  if (item.kind !== "message" || item.role !== "assistant") return NO_ARTIFACTS;
  const cached = artifactsByMessage.get(item);
  if (cached !== undefined) return cached;
  const found: ConversationArtifact[] = [];
  for (const [, text, href] of item.content.matchAll(MARKDOWN_LINK)) {
    const link = parseEntityLink(href);
    if (link === null || (link.type !== "artifact" && link.type !== "model")) continue;
    const resourceId = entityIdFromPath(link.path);
    if (resourceId === null) continue;
    found.push({ kind: "saved", id: resourceId, resourceId, name: text.trim() });
  }
  for (const [, code] of item.content.matchAll(SAVE_RESOURCE_BLOCK)) {
    const action = parseSaveResourceAction(code.trim());
    if (action?.content === undefined) continue;
    found.push({ kind: "draft", id: `draft:${action.fileName}`, name: action.fileName, content: action.content });
  }
  artifactsByMessage.set(item, found);
  return found;
}

function latestSaveOf(
  draft: Extract<ConversationArtifact, { kind: "draft" }>,
  savedArtifacts: SavedArtifactRef[],
): SavedArtifactRef | undefined {
  return savedArtifacts.reduce<SavedArtifactRef | undefined>(
    (latest, saved) => (saved.fileName === draft.name && saved.content === draft.content ? saved : latest),
    undefined,
  );
}
